import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheckIcon, ArchiveBoxIcon, DocumentTextIcon, DocumentDuplicateIcon,
  TruckIcon, PlusIcon, CheckCircleIcon,
  ArrowRightIcon, SparklesIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import { fetchMySupplierProfile, fetchMyListings, fetchSupplierOrders, fetchRfqs } from '../api/marketplaceApi';
import type { SupplierProfile, Listing, SupplierOrder, Rfq } from '../types';

const VERIFICATION_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  draft: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', label: 'Draft' },
  submitted: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Submitted' },
  under_review: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Under Review' },
  verified: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Verified' },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Rejected' },
  suspended: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Suspended' },
};

const SupplierOverview: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<SupplierProfile | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [rfqs, setRfqs] = useState<Rfq[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, listingsRes, ordersRes, rfqsRes] = await Promise.allSettled([
          fetchMySupplierProfile(),
          fetchMyListings(),
          fetchSupplierOrders({ limit: 5 }),
          fetchRfqs({ status: 'published', limit: 5 }),
        ]);
        if (profileRes.status === 'fulfilled') setProfile(profileRes.value.profile);
        if (listingsRes.status === 'fulfilled') setListings(listingsRes.value.items || []);
        if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.items || []);
        if (rfqsRes.status === 'fulfilled') setRfqs(rfqsRes.value.items || []);
      } catch (err) {
        console.error('Supplier dashboard load error:', err);
      }
    };
    load();
  }, []);

  const verificationStatus = profile?.verification_status || 'draft';
  const vStyle = VERIFICATION_STYLES[verificationStatus] || VERIFICATION_STYLES.draft;
  const activeListings = listings.filter(l => l.status === 'active');
  const pendingOrders = orders.filter(o => ['pending_supplier_confirmation', 'confirmed'].includes(o.status));
  const inFulfilment = orders.filter(o => ['preparing', 'in_transit', 'ready_for_dispatch'].includes(o.status));
  const completedOrders = orders.filter(o => o.status === 'completed');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-white rounded-full translate-y-1/2 -translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <SparklesIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Supplier Portal</h1>
              <p className="text-emerald-100 mt-1">Manage your listings, orders, and fulfilment</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!profile && (
              <button
                onClick={() => navigate('/marketplace/supplier/profile')}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-amber-600 rounded-xl hover:bg-emerald-50 font-bold transition-all shadow-lg"
              >
                <ShieldCheckIcon className="w-5 h-5" />
                Register as Supplier
              </button>
            )}
            {profile && verificationStatus !== 'verified' && (
              <button
                onClick={() => navigate('/marketplace/supplier/profile')}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-amber-600 rounded-xl hover:bg-emerald-50 font-bold transition-all shadow-lg"
              >
                <ShieldCheckIcon className="w-5 h-5" />
                Complete Verification
              </button>
            )}
            {verificationStatus === 'verified' && (
              <button
                onClick={() => navigate('/marketplace/supplier/listings/new')}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-700 rounded-xl hover:bg-emerald-50 font-bold transition-all shadow-lg"
              >
                <PlusIcon className="w-5 h-5" />
                New Listing
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Verification Banner */}
      {profile && verificationStatus !== 'verified' && (
        <div className={`rounded-2xl p-5 border ${vStyle.bg} ${vStyle.border}`}>
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className={`w-6 h-6 mt-0.5 ${vStyle.text}`} />
            <div>
              <h3 className={`text-sm font-extrabold ${vStyle.text}`}>Verification Status: {vStyle.label}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {verificationStatus === 'draft' && 'Complete your supplier profile and submit for verification to start receiving orders.'}
                {verificationStatus === 'submitted' && 'Your profile is being reviewed by our team. You will be notified once a decision is made.'}
                {verificationStatus === 'under_review' && 'Your profile is currently being reviewed by our team.'}
                {verificationStatus === 'rejected' && `Reason: ${profile.verification_reason || 'Not specified'}. Please update your profile and resubmit.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          { label: 'Active Listings', value: activeListings.length, icon: ArchiveBoxIcon, color: 'from-emerald-500 to-teal-500', light: 'bg-emerald-50' },
          { label: 'Pending Orders', value: pendingOrders.length, icon: ClockIcon, color: 'from-amber-500 to-orange-500', light: 'bg-amber-50' },
          { label: 'In Fulfilment', value: inFulfilment.length, icon: TruckIcon, color: 'from-blue-500 to-indigo-500', light: 'bg-blue-50' },
          { label: 'Completed', value: completedOrders.length, icon: CheckCircleIcon, color: 'from-green-500 to-emerald-500', light: 'bg-green-50' },
          { label: 'RFQs', value: rfqs.length, icon: DocumentTextIcon, color: 'from-purple-500 to-pink-500', light: 'bg-purple-50' },
          { label: 'Verification', value: vStyle.label, icon: ShieldCheckIcon, color: verificationStatus === 'verified' ? 'from-green-500 to-emerald-500' : 'from-amber-500 to-orange-500', light: verificationStatus === 'verified' ? 'bg-green-50' : 'bg-amber-50', isText: true },
        ].map((kpi, i) => (
          <div key={i} className={`${kpi.light} rounded-2xl p-4 border border-white/50`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-gradient-to-br ${kpi.color} rounded-xl flex items-center justify-center shadow-md`}>
                <kpi.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-extrabold text-gray-900">{kpi.value}</p>
                <p className="text-xs text-gray-500 font-medium">{kpi.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RFQ Opportunities */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-purple-500" />
              <h3 className="font-extrabold text-gray-900">RFQ Opportunities</h3>
            </div>
            <button
              onClick={() => navigate('/marketplace/supplier/rfqs')}
              className="flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              View all <ArrowRightIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {rfqs.length === 0 ? (
              <div className="p-8 text-center">
                <DocumentTextIcon className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 font-medium text-sm">No open RFQs</p>
                <p className="text-gray-400 text-xs mt-1">When buyers create RFQs matching your products, they will appear here.</p>
              </div>
            ) : (
              rfqs.map(rfq => (
                <button
                  key={rfq.id}
                  onClick={() => navigate(`/marketplace/supplier/rfqs/${rfq.id}`)}
                  className="w-full px-6 py-4 flex items-center gap-3 hover:bg-gray-50 text-left transition-colors"
                >
                  <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center shrink-0">
                    <DocumentTextIcon className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{rfq.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{rfq.rfq_number} · {rfq.buyer_org_name || 'Buyer'}</p>
                  </div>
                  <StatusBadge status={rfq.status} size="sm" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Recent Supplier Orders */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentDuplicateIcon className="w-5 h-5 text-emerald-500" />
              <h3 className="font-extrabold text-gray-900">Recent Orders</h3>
            </div>
            <button
              onClick={() => navigate('/marketplace/supplier/orders')}
              className="flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              View all <ArrowRightIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {orders.length === 0 ? (
              <div className="p-8 text-center">
                <DocumentDuplicateIcon className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 font-medium text-sm">No orders yet</p>
                <p className="text-gray-400 text-xs mt-1">Orders from buyers will appear here once you receive them.</p>
              </div>
            ) : (
              orders.map(order => (
                <button
                  key={order.id}
                  onClick={() => navigate(`/marketplace/supplier/orders/${order.id}`)}
                  className="w-full px-6 py-4 flex items-center gap-3 hover:bg-gray-50 text-left transition-colors"
                >
                  <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
                    <DocumentDuplicateIcon className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{order.order_number}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{order.parent_order_number}</p>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={order.status} size="sm" />
                    <p className="text-xs font-mono text-gray-500 mt-0.5">
                      <Money amount={order.total_amount} currency={order.currency} />
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierOverview;
